/**
 * Shared utilities for WrenchMode event log parsing and timer formatting.
 * Ported from mobile: /open-wrench-contractor-mobile-client/utils/wrenchModeEventUtils.js
 */
import moment from 'moment-timezone';
import { FINISHED_WORK_ORDER_DISPLAY_STATUSES } from '../constants/WorkOrderDisplayStatuses';
import { nullSafeGet } from './DataAccessUtils';
import { myTimeZone } from './DataFormatterUtils';

export interface WrenchModeEvent {
	id: number;
	eventType: string;
	createdAt: string;
	locationId: number;
	scheduleId: number;
	workOrderId?: number;
	serviceCallId?: number;
	checkoutStatus?: string;
	checkoutReason?: string;
	note?: string;
}

export interface WorkSession {
	events: WrenchModeEvent[];
	startTime: number;
	sessionMs: number;
	// Open check-in (started_work with no ended_work yet). Counts 0 toward totals until the
	// checkout lands — renderers show an "In progress" affordance instead of a zero duration.
	inProgress?: boolean;
}

export interface VisitSession {
	events: WrenchModeEvent[];
	driveEvents: WrenchModeEvent[];
	timerEvents: WrenchModeEvent[];
	workLogEvents: WrenchModeEvent[];
	driveTimeMs: number;
	workSessions: WorkSession[];
	workTimeMs: number;
	totalMs: number;
	inProgress?: boolean;
}

export interface ParsedLocationEvents {
	driveEvents: WrenchModeEvent[];
	driveTimeMs: number;
	timerEvents: WrenchModeEvent[];
	workLogEvents: WrenchModeEvent[];
	totalWorkMs: number;
	workSessions: WorkSession[];
	visitSessions: VisitSession[];
}

export const EMPTY_PARSED: ParsedLocationEvents = {
	driveEvents: [],
	driveTimeMs: 0,
	timerEvents: [],
	workLogEvents: [],
	totalWorkMs: 0,
	workSessions: [],
	visitSessions: [],
};

export const hasLocationEvents = (parsed: ParsedLocationEvents | undefined): boolean => {
	if (!parsed) return false;
	return (
		parsed.workLogEvents.length > 0 ||
		parsed.driveEvents.length > 0 ||
		parsed.timerEvents.length > 0
	);
};

/**
 * Collect all events from a hydrated schedule (location-level + schedule-level).
 */
export const collectScheduleEvents = (schedule: any): WrenchModeEvent[] => {
	const allEvents: WrenchModeEvent[] = [];
	const locations = schedule.locations || [];
	for (const loc of locations) {
		const locEvents = loc.events || [];
		allEvents.push.apply(allEvents, locEvents);
	}
	const scheduleEvents = schedule.events || [];
	allEvents.push.apply(allEvents, scheduleEvents);
	return allEvents;
};

const matchesTech = (sc, techEmail: string): boolean =>
	sc.leadTechnicianEmail === techEmail || (sc.additionalTechnicianEmails || []).includes(techEmail);

const sameDay = (value: string | number | null | undefined, date: string): boolean => {
	if (value === null || value === undefined || value === '') return false;
	// Accepts ISO strings or epoch ms (number or all-digit string).
	// `date` is "YYYY-MM-DD" in supplier-local TZ; moment uses the same TZ.
	const str = String(value);
	const m = /^\d+$/.test(str) ? moment(Number(str)) : moment(str);
	return m.isValid() && m.format('YYYY-MM-DD') === date;
};

const isWOStatusTerminal = (wo): boolean => {
	return (
		!!wo &&
		!!wo.displayStatus &&
		FINISHED_WORK_ORDER_DISPLAY_STATUSES.indexOf(wo.displayStatus) !== -1
	);
};

const techServiceCallsOnDate = (wo, techEmail: string, dateKey: string): any[] => {
	if (!wo || !techEmail || !dateKey) return [];
	return (wo.associatedServiceCalls || []).filter(
		(sc) =>
			matchesTech(sc, techEmail) &&
			sc.serviceScheduledAt &&
			moment(sc.serviceScheduledAt).format('YYYY-MM-DD') === dateKey
	);
};

const techCheckedInSCsOnDate = (wo, techEmail: string, dateKey: string): any[] => {
	return techServiceCallsOnDate(wo, techEmail, dateKey).filter((sc) => !!sc.checkInTime);
};

const hasPendingReturnVisit = (wo, techEmail: string, dateKey: string): boolean => {
	const scs = techServiceCallsOnDate(wo, techEmail, dateKey);
	const checkedIn = scs.filter((sc) => !!sc.checkInTime);
	if (checkedIn.length === 0) return false;
	const latestCheckout = checkedIn.reduce((max, sc) => {
		if (!sc.checkOutTime) return max;
		const t = new Date(sc.checkOutTime).getTime();
		return t > max ? t : max;
	}, 0);
	if (!latestCheckout) return false;
	return scs.some(
		(sc) =>
			!sc.checkInTime &&
			sc.serviceScheduledAt &&
			new Date(sc.serviceScheduledAt).getTime() > latestCheckout
	);
};

const isWOWrappedUpForTech = (wo, techEmail: string, dateKey: string): boolean => {
	if (isWOStatusTerminal(wo)) return true;
	const techSCs = techServiceCallsOnDate(wo, techEmail, dateKey);
	const checkedIn = techSCs.filter((sc) => !!sc.checkInTime);
	if (checkedIn.length === 0) return false;
	const lastSC = wo.lastServiceCall;
	if (!lastSC || !lastSC.checkOutTime) return false;
	return !hasPendingReturnVisit(wo, techEmail, dateKey);
};

const isWOStartedForTech = (wo, techEmail: string, dateKey: string): boolean => {
	if (isWOStatusTerminal(wo)) return false;
	const lastSC = wo.lastServiceCall;
	if (lastSC && lastSC.checkOutTime) return false;
	const checkedIn = techCheckedInSCsOnDate(wo, techEmail, dateKey);
	return checkedIn.some((sc) => !sc.checkOutTime);
};

export const countCompletedWorkOrders = (workOrders, techEmail: string, date: string): number => {
	let count = 0;
	for (const wo of workOrders) {
		if (!wo) continue;
		if (isWOWrappedUpForTech(wo, techEmail, date)) count++;
	}
	return count;
};

export const partitionWorkOrders = (
	workOrders: any[],
	techEmail: string,
	date: string
): { notStarted: any[]; started: any[]; ended: any[] } => {
	const notStarted: any[] = [];
	const started: any[] = [];
	const ended: any[] = [];
	for (const wo of workOrders) {
		if (!wo) continue;
		if (isWOWrappedUpForTech(wo, techEmail, date)) {
			ended.push(wo);
		} else if (isWOStartedForTech(wo, techEmail, date)) {
			started.push(wo);
		} else {
			notStarted.push(wo);
		}
	}
	return { notStarted, started, ended };
};

export const formatTimer = (ms: number): string => {
	if (ms <= 0) return '00:00:00';
	const totalSeconds = Math.floor(ms / 1000);
	const hours = String(Math.floor(totalSeconds / 3600)).padStart(2, '0');
	const minutes = String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, '0');
	const seconds = String(totalSeconds % 60).padStart(2, '0');
	return `${hours}:${minutes}:${seconds}`;
};

export const formatDuration = (ms: number): string => {
	if (ms <= 0) return '--';

	if (ms < 60000) {
		const seconds = Math.floor(ms / 1000);
		return `${seconds}s`;
	}

	const totalMinutes = Math.floor(ms / 60000);
	const hours = Math.floor(totalMinutes / 60);
	const minutes = totalMinutes % 60;

	if (hours > 0 && minutes > 0) return `${hours}h ${minutes}m`;
	if (hours > 0) return `${hours}h`;

	return `${minutes}m`;
};

// Event times read in the timezone of the location where the work happened, not the
// viewer's — a dispatcher in New York reading a California tech's log should see the clock
// the tech saw on site. The zone abbreviation is always shown so the reading is unambiguous
// when the two differ.
//
// The timeline files rows under the schedule's date, which is not always the event's calendar
// day at the site: a site far enough from the supplier's zone can be into the next day (or
// still in the previous one) at the moment the event is logged. A bare clock would then sit
// under a heading that contradicts it with nothing to show for it, so pass `scheduleDate`
// (YYYY-MM-DD) and the site-local date is appended on exactly the rows where the two disagree.
export const formatEventTime = (
	createdAt: string,
	timeZone: string,
	scheduleDate?: string
): string => {
	const siteTime = moment(createdAt).tz(timeZone);
	const clock = siteTime.format('h:mmA z');
	if (!scheduleDate || siteTime.format('YYYY-MM-DD') === scheduleDate) {
		return clock;
	}
	return `${clock} (${siteTime.format('MMM D')})`;
};

// A location without a timeZoneId (never geocoded) has no site clock to show, so the
// viewer's zone stands in — labelled with its own abbreviation, so it still reads honestly.
export const locationTimeZone = (location: any): string =>
	nullSafeGet('timeZoneId', location) || myTimeZone();

export const EVENT_LABELS: Record<string, string> = {
	began_trip: 'Began Trip',
	trip_paused: 'Trip Paused',
	trip_resumed: 'Trip Resumed',
	arrived: 'Arrived at Destination',
	trip_cancelled: 'Trip Cancelled',
	drive_note: 'Added Note',
	started_work: 'Started Work',
	ended_work: 'Finished Work',
	location_started: 'Started Work',
	location_paused: 'Paused Work',
	location_resumed: 'Resumed Work',
	location_ended: 'Ended Work at Location',
};

/**
 * Filter a flat events array to only events for a specific location.
 */
export const filterEventsByLocation = (
	events: WrenchModeEvent[],
	locationId: number
): WrenchModeEvent[] => {
	const locStr = String(locationId);
	return events.filter((e) => String(e.locationId) === locStr);
};

const DRIVE_TYPES = new Set([
	'began_trip',
	'trip_paused',
	'trip_resumed',
	'arrived',
	'trip_cancelled',
	'drive_note',
]);
const TIMER_TYPES = new Set([
	'location_started',
	'location_paused',
	'location_resumed',
	'location_ended',
]);

interface InternalVisit {
	events: WrenchModeEvent[];
	driveEvents: WrenchModeEvent[];
	timerEvents: WrenchModeEvent[];
	workLogEvents: WrenchModeEvent[];
	hasDrive: boolean;
	hasArrival: boolean;
}

const ms = (iso: string): number => new Date(iso).getTime();

const sumIntervals = (intervals: Array<[number, number]>): number =>
	intervals.reduce((sum, [s, e]) => sum + (e - s), 0);

/**
 * The closed [start, stop] segments of a WrenchMode location timer —
 * `location_started`/`location_resumed` through `location_paused`/`location_ended`.
 *
 * A trailing segment whose stop event never arrived is omitted: the tech is either still on
 * the clock or forgot to stop it, and neither case has a trustworthy end timestamp. Time
 * already banked by an explicit stop survives regardless, so a session that was paused and
 * then abandoned keeps everything it recorded up to that pause.
 */
const runningSegments = (events: WrenchModeEvent[]): Array<[number, number]> => {
	const sorted = [...events].sort((a, b) => ms(a.createdAt) - ms(b.createdAt));
	const segments: Array<[number, number]> = [];
	let segmentStart: number | null = null;
	for (const evt of sorted) {
		const stamp = ms(evt.createdAt);
		if (evt.eventType === 'location_started' || evt.eventType === 'location_resumed') {
			if (segmentStart === null) segmentStart = stamp;
		} else if (evt.eventType === 'location_paused' || evt.eventType === 'location_ended') {
			if (segmentStart !== null) {
				segments.push([segmentStart, stamp]);
				segmentStart = null;
			}
		}
	}
	return segments;
};

/** Counted duration of one timer session: its running time, excluding pause gaps. */
const timerSessionMs = (events: WrenchModeEvent[]): number => sumIntervals(runningSegments(events));

/**
 * Pair `started_work` → `ended_work` events by serviceCallId (preferred)
 * or workOrderId fallback; leftovers on mismatched keys (legacy events missing
 * serviceCallId on one side) are reconciled by workOrderId. Unmatched `started_work`
 * events (checked in, no checkout yet) are emitted after the closed pairs with no
 * `ended` — dropping them would hide the check-in entirely until someone checks the
 * tech out.
 */
const pairStartedEnded = (
	events: WrenchModeEvent[]
): Array<{ started: WrenchModeEvent; ended?: WrenchModeEvent }> => {
	const sorted = events
		.filter((e) => e.eventType === 'started_work' || e.eventType === 'ended_work')
		.sort((a, b) => ms(a.createdAt) - ms(b.createdAt));

	const keyOf = (evt: WrenchModeEvent): string =>
		evt.serviceCallId != null ? `sc_${evt.serviceCallId}` : `wo_${evt.workOrderId}`;

	const open = new Map<string, WrenchModeEvent>();
	const pairs: Array<{ started: WrenchModeEvent; ended?: WrenchModeEvent }> = [];
	const unmatchedEnded: WrenchModeEvent[] = [];
	for (const evt of sorted) {
		const k = keyOf(evt);
		if (evt.eventType === 'started_work') {
			open.set(k, evt);
		} else {
			const start = open.get(k);
			if (start) {
				pairs.push({ started: start, ended: evt });
				open.delete(k);
			} else {
				unmatchedEnded.push(evt);
			}
		}
	}
	// Legacy events can carry serviceCallId on only one side of a pair (the other keys by
	// workOrderId), leaving both sides unmatched above. Reconcile by workOrderId so a
	// checked-out visit is never emitted as still open.
	open.forEach((started, startKey) => {
		if (started.workOrderId == null) return;
		const endedIndex = unmatchedEnded.findIndex(
			(ended) =>
				ended.workOrderId === started.workOrderId && ms(ended.createdAt) >= ms(started.createdAt)
		);
		if (endedIndex !== -1) {
			pairs.push({ started, ended: unmatchedEnded[endedIndex] });
			unmatchedEnded.splice(endedIndex, 1);
			open.delete(startKey);
		}
	});
	open.forEach((started) => {
		pairs.push({ started });
	});
	return pairs;
};

/**
 * Parse eventLog for a location into structured drive/work data.
 *
 * Hybrid rules:
 *   - WM-tracked work: the running segments of the location timer, i.e.
 *     location_started/location_resumed → location_paused/location_ended. A session that
 *     never got a location_ended still counts everything up to its last pause; only the
 *     open tail after that pause is dropped, since it has no trustworthy end timestamp.
 *   - Non-WM work: when a location has no closed timer segments at all, each
 *     started_work → ended_work pair becomes its own VisitSession with empty drive/timer
 *     arrays. When the location does use a timer, those events are injected into the timer
 *     visits for display only and contribute no time of their own — the timer is the
 *     source of truth and counting both would double-count the same minutes.
 *   - An open check-in (started_work with no ended_work) always counts 0: its elapsed time
 *     is unbounded, so a forgotten checkout would otherwise inflate the row indefinitely.
 *   - totalWorkMs = sum of every VisitSession's workTimeMs, and is the single source of
 *     truth for "work time" across the timeline, logs table, and schedules table.
 *   - Drive time stays WM-only (began_trip → arrived, minus trip pauses).
 *     Gap-derived drive is computed at the row-aggregation layer because it
 *     needs the cross-location SC list.
 */
export const parseLocationEvents = (events: WrenchModeEvent[]): ParsedLocationEvents => {
	const driveEvents: WrenchModeEvent[] = [];
	const workLogEvents: WrenchModeEvent[] = [];
	const timerEvents: WrenchModeEvent[] = [];

	for (const evt of events) {
		switch (evt.eventType) {
			case 'began_trip':
			case 'trip_paused':
			case 'trip_resumed':
			case 'arrived':
			case 'trip_cancelled':
			case 'drive_note':
				driveEvents.push(evt);
				break;
			case 'started_work':
			case 'ended_work':
				workLogEvents.push(evt);
				break;
			case 'location_started':
			case 'location_paused':
			case 'location_resumed':
			case 'location_ended':
				timerEvents.push(evt);
				break;
		}
	}

	const allEvents = [...events].sort((a, b) => ms(a.createdAt) - ms(b.createdAt));

	// ─── WM work intervals from location_* (used for "does this location use a timer?") ──
	const wmWorkIntervals = runningSegments(timerEvents);

	// ─── Group drive + timer events into visits ───────────────────────────────────
	// Work events are excluded from the grouper and injected afterward so that
	// a started_work before location_started doesn't create a phantom visit.
	const wmGroupingEvents = allEvents.filter(
		(evt) => evt.eventType !== 'started_work' && evt.eventType !== 'ended_work'
	);

	const internalVisits: InternalVisit[] = [];
	let currentVisit: InternalVisit | null = null;

	for (const evt of wmGroupingEvents) {
		const isVisitStart =
			evt.eventType === 'began_trip' ||
			(evt.eventType === 'arrived' && (!currentVisit || !currentVisit.hasDrive)) ||
			(evt.eventType === 'location_started' &&
				(!currentVisit || (!currentVisit.hasDrive && !currentVisit.hasArrival)));

		if (isVisitStart) {
			if (currentVisit) internalVisits.push(currentVisit);
			currentVisit = {
				events: [],
				driveEvents: [],
				timerEvents: [],
				workLogEvents: [],
				hasDrive: evt.eventType === 'began_trip',
				hasArrival: evt.eventType === 'arrived',
			};
		}

		if (!currentVisit) {
			currentVisit = {
				events: [],
				driveEvents: [],
				timerEvents: [],
				workLogEvents: [],
				hasDrive: false,
				hasArrival: false,
			};
		}

		currentVisit.events.push(evt);
		if (DRIVE_TYPES.has(evt.eventType)) {
			currentVisit.driveEvents.push(evt);
			if (evt.eventType === 'began_trip') currentVisit.hasDrive = true;
			if (evt.eventType === 'arrived') currentVisit.hasArrival = true;
		} else if (TIMER_TYPES.has(evt.eventType)) {
			currentVisit.timerEvents.push(evt);
		}
	}
	if (currentVisit) internalVisits.push(currentVisit);

	// ─── Inject work events into visits by nearest time range ────────────────────
	// When WM sessions exist, all work events belong to them — boundaries stretch
	// to fit. When no WM sessions exist, work events become standalone visits below.
	const hasWmSessions = wmWorkIntervals.length > 0;

	// Pair check-ins with checkouts once, up front. An open check-in (a started_work with no
	// ended_work yet) counts 0 toward totals and is surfaced separately below. It must never be
	// injected into a timer visit: the boundary-extension fallback would add the gap between the
	// check-in and the nearest session as phantom work time (e.g. a check-out at 10:00 plus an
	// open check-in at 14:00 would report 4h of extra work).
	const workLogPairs = pairStartedEnded(workLogEvents);
	const openCheckInEvents = new Set<WrenchModeEvent>(
		workLogPairs.filter((pair) => !pair.ended).map((pair) => pair.started)
	);

	const eventsToInject = hasWmSessions
		? workLogEvents.filter((evt) => !openCheckInEvents.has(evt))
		: [];
	for (const woEvt of eventsToInject) {
		const woTime = ms(woEvt.createdAt);
		let bestVisit: InternalVisit | null = null;
		let bestDist = Infinity;
		for (const visit of internalVisits) {
			if (visit.events.length === 0) continue;
			const visitStart = ms(visit.events[0].createdAt);
			const visitEnd = ms(visit.events[visit.events.length - 1].createdAt);
			if (woTime >= visitStart && woTime <= visitEnd) {
				bestVisit = visit;
				break;
			}
			const dist = Math.min(Math.abs(woTime - visitStart), Math.abs(woTime - visitEnd));
			if (dist < bestDist) {
				bestDist = dist;
				bestVisit = visit;
			}
		}
		if (bestVisit) {
			bestVisit.events.push(woEvt);
			bestVisit.workLogEvents.push(woEvt);
		}
	}

	// ─── Compute per-WM-visit drive + work sessions (existing math) ────────────────
	let driveTimeMsTotal = 0;
	const visitSessions: VisitSession[] = [];
	// Visits whose WM timer is still running (a location_started with no location_ended). An open
	// check-in that coincides with one belongs to that live visit, not a separate synthesized one.
	const liveTimerVisits: VisitSession[] = [];

	for (const visit of internalVisits) {
		// Drive time
		const beganTrip = visit.driveEvents.find((e) => e.eventType === 'began_trip');
		const arrived = visit.driveEvents.find((e) => e.eventType === 'arrived');
		const tripCancelled = visit.driveEvents.find((e) => e.eventType === 'trip_cancelled');
		const driveEndEvent = arrived || tripCancelled;
		let visitDriveTimeMs = 0;
		if (beganTrip && driveEndEvent) {
			const totalMs = ms(driveEndEvent.createdAt) - ms(beganTrip.createdAt);
			let tripPausedMs = 0;
			let lastTripPause: number | null = null;
			for (const evt of visit.driveEvents) {
				if (evt.eventType === 'trip_paused') {
					lastTripPause = ms(evt.createdAt);
				} else if (
					(evt.eventType === 'trip_resumed' ||
						evt.eventType === 'arrived' ||
						evt.eventType === 'trip_cancelled') &&
					lastTripPause
				) {
					tripPausedMs += ms(evt.createdAt) - lastTripPause;
					lastTripPause = null;
				}
			}
			visitDriveTimeMs = totalMs - tripPausedMs;
		}
		driveTimeMsTotal += visitDriveTimeMs;

		// Work sessions: location_started → location_ended (with pauses)
		const workSessions: WorkSession[] = [];
		let curSession: { events: WrenchModeEvent[]; startTime: number } | null = null;
		const pushSession = (session: { events: WrenchModeEvent[]; startTime: number }): void => {
			workSessions.push({
				events: session.events,
				startTime: session.startTime,
				sessionMs: timerSessionMs(session.events),
			});
		};
		for (const evt of visit.timerEvents) {
			if (evt.eventType === 'location_started') {
				curSession = { events: [evt], startTime: ms(evt.createdAt) };
			} else if (curSession) {
				curSession.events.push(evt);
				if (evt.eventType === 'location_ended') {
					pushSession(curSession);
					curSession = null;
				}
			}
		}
		// A session that never got a location_ended still banks the time it recorded before its
		// last explicit stop — a tech who paused and forgot to end the day keeps those hours.
		// Only the open tail after that stop is dropped (see runningSegments).
		if (curSession) pushSession(curSession);

		// Inject workLogEvents into matching work sessions for display.
		// If a work event falls before the first session (e.g. a revised
		// check-in time earlier than location_started), attach it to the
		// earliest session so it is not orphaned from the timeline.
		for (const woEvt of visit.workLogEvents) {
			const woTime = ms(woEvt.createdAt);
			let injected = false;
			for (const session of workSessions) {
				const endEvt = session.events.find((e) => e.eventType === 'location_ended');
				const sessionEnd = endEvt ? ms(endEvt.createdAt) : Infinity;
				if (woTime >= session.startTime && woTime <= sessionEnd) {
					session.events.push(woEvt);
					injected = true;
					break;
				}
			}
			if (!injected && workSessions.length > 0) {
				if (woTime < workSessions[0].startTime) {
					workSessions[0].sessionMs += workSessions[0].startTime - woTime;
					workSessions[0].startTime = woTime;
					workSessions[0].events.push(woEvt);
				} else {
					const lastSession = workSessions[workSessions.length - 1];
					const lastSessionEnd = lastSession.events.reduce((max, e) => {
						const t = ms(e.createdAt);
						return t > max ? t : max;
					}, lastSession.startTime);
					if (woTime > lastSessionEnd) {
						lastSession.sessionMs += woTime - lastSessionEnd;
					}
					lastSession.events.push(woEvt);
				}
			}
		}
		for (const session of workSessions) {
			session.events.sort((a, b) => ms(a.createdAt) - ms(b.createdAt));
		}

		const visitWorkTimeMs = workSessions.reduce((sum, s) => sum + (s.sessionMs || 0), 0);

		const builtVisit: VisitSession = {
			events: visit.events,
			driveEvents: visit.driveEvents,
			timerEvents: visit.timerEvents,
			workLogEvents: visit.workLogEvents,
			driveTimeMs: visitDriveTimeMs,
			workSessions,
			workTimeMs: visitWorkTimeMs,
			totalMs: visitDriveTimeMs + visitWorkTimeMs,
		};
		visitSessions.push(builtVisit);
		// `curSession` is left non-null only when the last timer session never closed — the tech
		// is still on the clock at this location.
		if (curSession) liveTimerVisits.push(builtVisit);
	}

	// ─── Synthesize / attach standalone check-in sessions ────────────────────────
	for (const { started, ended } of workLogPairs) {
		if (ended) {
			// Closed check-in/out pair. When the location has WM timer sessions these events were
			// already injected into a timer visit above; only stand them up as their own visit
			// when there are no timer sessions to absorb them.
			if (hasWmSessions) continue;
			const startMs = ms(started.createdAt);
			const endMs = ms(ended.createdAt);
			const sessionMs = Math.max(0, endMs - startMs);
			const sessionEvents = [started, ended];
			const session: WorkSession = { events: sessionEvents, startTime: startMs, sessionMs };
			visitSessions.push({
				events: sessionEvents,
				driveEvents: [],
				timerEvents: [],
				workLogEvents: sessionEvents,
				driveTimeMs: 0,
				workSessions: [session],
				workTimeMs: sessionMs,
				totalMs: sessionMs,
			});
			continue;
		}

		// Open check-in (no checkout yet): counts 0 toward totals until the checkout lands — its
		// elapsed time is unbounded (a forgotten checkout could be days old), so counting
		// "now - start" as worked hours would inflate historical rows. It stays visible with an
		// "In progress" affordance instead of a zero duration.
		//
		//   • If a WM timer is still running, the check-in belongs to that live visit — mark the
		//     visit in progress and show the check-in row inside it, rather than duplicating the
		//     active visit as a second "In progress" session.
		//   • Otherwise (an earlier session already checked out, or the tech uses no timer at all)
		//     surface the check-in as its own standalone in-progress visit.
		const liveVisit =
			liveTimerVisits.length > 0 ? liveTimerVisits[liveTimerVisits.length - 1] : null;
		if (liveVisit) {
			liveVisit.inProgress = true;
			liveVisit.workLogEvents = liveVisit.workLogEvents.concat(started);
			const lastSession = liveVisit.workSessions[liveVisit.workSessions.length - 1];
			// Display-only: surface the check-in event without adding to the session's counted time.
			if (lastSession) lastSession.events = lastSession.events.concat(started);
			continue;
		}

		const startMs = ms(started.createdAt);
		const session: WorkSession = {
			events: [started],
			startTime: startMs,
			sessionMs: 0,
			inProgress: true,
		};
		visitSessions.push({
			events: [started],
			driveEvents: [],
			timerEvents: [],
			workLogEvents: [started],
			driveTimeMs: 0,
			workSessions: [session],
			workTimeMs: 0,
			totalMs: 0,
			inProgress: true,
		});
	}

	// Sort visits chronologically by their first event
	visitSessions.sort((a, b) => {
		const aStart = a.events.length > 0 ? ms(a.events[0].createdAt) : 0;
		const bStart = b.events.length > 0 ? ms(b.events[0].createdAt) : 0;
		return aStart - bStart;
	});

	// totalWorkMs = sum of all visit work times (sessions already include extensions)
	const totalWorkMs = visitSessions.reduce((sum, v) => sum + v.workTimeMs, 0);

	return {
		driveEvents,
		driveTimeMs: driveTimeMsTotal,
		timerEvents,
		workLogEvents,
		totalWorkMs,
		workSessions: visitSessions.reduce((acc: WorkSession[], v) => acc.concat(v.workSessions), []),
		visitSessions,
	};
};
